import { fetchWithAuth } from '../utils/auth';
import { getReportApiBaseUrl } from '../config/api';

const LIVE_API_BASE_URL = 'https://live.tezda.tech';
const LIVE_CDN_BASE_URL = 'https://cdn2.pingtop.co';

export type LiveAccessMode = 'GLOBAL' | 'ALLOWLIST';
export type LiveAccessApplicationStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'BLOCKED' | 'SUSPENDED';
export type LiveSessionPublishStatus = 'PUBLISHED' | 'UNPUBLISHED';

export interface LiveHostSummary {
  UserID: string;
  UserName?: string | null;
  Name?: string | null;
  ProfileImageURL?: string | null;
}

export type LiveSessionHostSummary = LiveHostSummary;

export interface LiveSessionPolicy {
  chatEnabled: boolean;
  slowModeSeconds: number;
  followersOnlyChat: boolean;
}

export interface LiveSessionStats {
  TotalUniqueViewers?: number;
  TotalLikes?: number;
  TopSenders?: { UserID: string; TotalSent: number }[];
  TopLikers?: { UserID: string; TotalLikes: number }[];
}

export interface LiveSession {
  SessionID: string;
  HostUserID: string;
  RoomName?: string | null;
  Title: string;
  Category?: string | null;
  CoverUrl?: string | null;
  ContentRating?: string | null;
  LifecycleStatus: string;
  PublishStatus?: string | null;
  Policy?: LiveSessionPolicy | null;
  StartedAt?: string | null;
  EndedAt?: string | null;
  RecordingKey?: string | null;
  RecordingStatus?: string | null;
  RecordingDurationSeconds?: number;
  RecordingPublishedVideoID?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  recordingUrl?: string | null;
  Stats?: LiveSessionStats | null;
  host?: LiveHostSummary | null;
  NotificationStats?: { totalSent?: number; devicesReached?: number; sent?: boolean; skipped?: boolean } | null;
}

export type LiveSessionAdminRecord = LiveSession;

export interface LiveReportedSessionSummary {
  sessionId: string;
  hostUserId: string;
  sessionTitle?: string | null;
  sessionLifecycleStatus: string;
  coverUrl?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  reportCount: number;
  reporterCount: number;
  lastReportedAt?: string | null;
  host?: LiveHostSummary | null;
}

export interface LiveSessionReportRecord {
  ReportID?: string;
  SessionID: string;
  HostUserID: string;
  ReporterUserID?: string | null;
  ReporterUserName?: string | null;
  ReasonCode?: string | null;
  ReasonLabel?: string | null;
  Details?: string | null;
  ScreenshotUrls: string[];
  BlockHost?: boolean;
  // Absent on every report written before host-side reporting existed, and all
  // of those are about the host. Render absence as "the host", never "unknown".
  ReportedUserID?: string | null;
  ReportedUserName?: string | null;
  ReportedUserRole?: 'HOST' | 'COHOST' | 'VIEWER' | null;
  ReporterRole?: 'HOST' | 'MODERATOR' | 'VIEWER' | null;
  SessionTitle?: string | null;
  SessionLifecycleStatus?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface LiveAccessConfig {
  mode: LiveAccessMode;
  allowlist: string[];
  updatedAt?: string;
  updatedBy?: string;
}

export interface LiveAccessAppeal {
  message: string;
  screenshotUrls: string[];
  submittedAt: string;
}

export interface LiveAccessApplication {
  UserID: string;
  UserName?: string | null;
  Status: LiveAccessApplicationStatus;
  AppliedAt?: string;
  ReviewedAt?: string | null;
  ReviewedBy?: string | null;
  Note?: string | null;
  ReapplyCount?: number;
  AppealMessage?: string | null;
  AppealSubmittedAt?: string | null;
  Appeals?: LiveAccessAppeal[];
  SuspendedAt?: string | null;
  SuspendedBy?: string | null;
  BlockedAt?: string | null;
  BlockedBy?: string | null;
}

export interface LiveAccessPolicyViolation {
  ViolationID: string;
  Code: string;
  Reason: string;
  Source: string;
  ModeratorUserID?: string;
  VideoID?: string;
  CreatedAt?: string;
  ExpiresAt?: string;
}

export interface LiveEligibilityCondition {
  id: string;
  label: string;
  description?: string;
  met: boolean;
  statusLabel?: string;
  current?: number;
  required?: number;
  applicationStatus?: string;
}

export interface LiveEligibilityResult {
  canGoLive: boolean;
  mode: LiveAccessMode;
  progress: {
    current: number;
    total: number;
    label: string;
  };
  nextAction: string;
  application: {
    status: string;
    canSubmit: boolean;
    isPending: boolean;
    isApproved: boolean;
    ctaLabel: string;
    rejectionNote?: string | null;
    appealSubmitted?: boolean;
    reapplyCount?: number;
  };
  requirements: LiveEligibilityCondition[];
  conditions: LiveEligibilityCondition[];
}

export interface LiveAccessProfileSummary {
  UserID: string;
  UserName?: string | null;
  Name?: string | null;
  ProfileImageURL?: string | null;
  FollowersCount?: number;
  AccountStatus?: string;
  IsPlusVerified?: boolean;
  activePolicyViolationCount: number;
  videosUrl?: string | null;
}

export interface LiveHealthAlert {
  severity: 'red' | 'yellow';
  metric: string;
  message: string;
  value: string;
}

export interface LiveHealthServicePod {
  name: string;
  phase: string;
  ready: boolean;
  restarts: number;
  startedAt?: string | null;
}

export interface LiveHealthService {
  name: string;
  namespace: string;
  deployment: string;
  available: boolean;
  healthy: boolean;
  error?: string;
  replicas?: {
    desired: number;
    available: number;
    ready: number;
    updated: number;
    unavailable: number;
  };
  pods?: LiveHealthServicePod[];
}

export interface LiveConnectionTelemetry {
  sessionId: string;
  userId: string;
  user: {
    userId: string;
    userName?: string | null;
    name?: string | null;
    email?: string | null;
    profileImageUrl?: string | null;
    profileImageThumbnailUrl?: string | null;
  } | null;
  socketId?: string;
  state: string;
  surface: string;
  provider: string;
  clientSentAt?: string | null;
  serverReceivedAt?: string | null;
  details?: Record<string, unknown>;
}

export interface LiveHealthSessionMetadata {
  sessionId: string;
  title?: string | null;
  hostUserId?: string | null;
  host?: {
    userId: string;
    userName?: string | null;
    name?: string | null;
    email?: string | null;
    profileImageUrl?: string | null;
    profileImageThumbnailUrl?: string | null;
  } | null;
  roomName?: string | null;
  lifecycleStatus?: string | null;
  publishStatus?: string | null;
  provider?: string | null;
  livekitServerUrl?: string | null;
  createdAt?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  inactiveAt?: string | null;
}

export interface LiveHealthSessionSummary {
  sessionId: string;
  hostHeartbeat: boolean;
  hostHeartbeatTtl: number;
  inactiveAt?: string | null;
  viewerCount: number;
  telemetryUserCount: number;
  metadata?: LiveHealthSessionMetadata | null;
  staleRedisSession?: boolean;
}

export interface LiveHealthOverview {
  generatedAt: string;
  services: LiveHealthService[];
  streamLag: {
    available: boolean;
    stream: string;
    group: string;
    consumers?: number;
    pending?: number;
    lag?: number | null;
    lastDeliveredId?: string | null;
    error?: string;
  };
  activeSessions: {
    total: number;
    sessions: LiveHealthSessionSummary[];
  };
  alerts: LiveHealthAlert[];
}

export interface LiveHealthSessionDetail {
  sessionId: string;
  hostHeartbeat: boolean;
  hostHeartbeatTtl: number;
  inactiveAt?: string | null;
  viewerCount: number;
  viewers: string[];
  telemetry: LiveConnectionTelemetry[];
  metadata?: LiveHealthSessionMetadata | null;
  staleRedisSession?: boolean;
}

export interface LiveAccessSubscriptionSummary {
  status?: string;
  periodType?: string;
  subscribedAt?: string | null;
  expiresAt?: string | null;
  isTrial: boolean;
}

export interface LiveAccessApplicationReviewDetails {
  application: LiveAccessApplication | null;
  profile: LiveAccessProfileSummary | null;
  eligibility: LiveEligibilityResult;
  activePolicyViolations: LiveAccessPolicyViolation[];
  subscription: LiveAccessSubscriptionSummary | null;
}

export interface LiveAccessApplicationsParams {
  status?: LiveAccessApplicationStatus | 'BLOCKED' | 'SUSPENDED';
  search?: string;
  page?: number;
  limit?: number;
}

export interface TopReporter {
  reporterUserId: string;
  reporterUserName: string | null;
  reportCount: number;
}

export interface LiveReportedSessionsParams {
  hostUserId?: string;
  sessionId?: string;
  page?: number;
  limit?: number;
}

export interface LiveAccessApplicationsResponse {
  applications: LiveAccessApplication[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface LiveSessionListParams {
  page?: number;
  limit?: number;
  publishStatus?: LiveSessionPublishStatus;
}

export interface LiveSessionListResponse {
  sessions: LiveSession[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface LiveReportedSessionsResponse {
  sessions: LiveReportedSessionSummary[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export type LiveReportedSessionListResponse = LiveReportedSessionsResponse;

export interface LiveSessionReportsResult {
  session: LiveSession | null;
  host: LiveHostSummary | null;
  reportCount: number;
  reporterCount: number;
  lastReportedAt?: string | null;
  reports: LiveSessionReportRecord[];
}

export type LiveSessionReportsDetail = LiveSessionReportsResult;

export interface UpdateLiveAccessConfigPayload {
  mode?: LiveAccessMode;
  addAllowlist?: string[];
  removeAllowlist?: string[];
}

export interface ReviewLiveAccessApplicationPayload {
  userId: string;
  status: LiveAccessApplicationStatus;
  note?: string;
}

const parseJson = async (response: Response) => {
  return response.json().catch(() => null);
};

const unwrapData = <T>(json: any): T => {
  return (json?.data ?? json) as T;
};

const getErrorMessage = (json: any, fallback: string) => {
  return json?.message || json?.error || fallback;
};

const request = async <T>(path: string, options: RequestInit = {}, fallbackError: string): Promise<T> => {
  const response = await fetchWithAuth(`${LIVE_API_BASE_URL}${path}`, options);
  const json = await parseJson(response);

  if (!response.ok) {
    throw new Error(getErrorMessage(json, fallbackError));
  }

  return unwrapData<T>(json);
};

const reportRequest = async <T>(path: string, options: RequestInit = {}, fallbackError: string): Promise<T> => {
  const response = await fetchWithAuth(`${getReportApiBaseUrl()}${path}`, options);
  const json = await parseJson(response);

  if (!response.ok) {
    throw new Error(getErrorMessage(json, fallbackError));
  }

  return unwrapData<T>(json);
};

const normalizeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  return Array.from(
    new Set(
      value
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  );
};

const normalizeHost = (raw: any): LiveHostSummary | null => {
  if (!raw) return null;

  return {
    UserID: raw?.UserID || raw?.userId || '',
    UserName: raw?.UserName || raw?.userName || null,
    Name: raw?.Name || raw?.name || null,
    ProfileImageURL: raw?.ProfileImageURL || raw?.profileImageURL || null,
  };
};

const normalizePolicy = (raw: any): LiveSessionPolicy | null => {
  if (!raw) return null;

  return {
    chatEnabled: Boolean(raw?.chatEnabled),
    slowModeSeconds: Number(raw?.slowModeSeconds ?? 0),
    followersOnlyChat: Boolean(raw?.followersOnlyChat),
  };
};

const normalizeSession = (raw: any): LiveSession => {
  const recordingKey = raw?.RecordingKey || raw?.recordingKey || null;

  return {
    SessionID: raw?.SessionID || raw?.sessionId || '',
    HostUserID: raw?.HostUserID || raw?.hostUserId || '',
    RoomName: raw?.RoomName || raw?.roomName || null,
    Title: raw?.Title || raw?.title || 'Untitled live',
    Category: raw?.Category || raw?.category || null,
    CoverUrl: raw?.CoverUrl || raw?.coverUrl || null,
    ContentRating: raw?.ContentRating || raw?.contentRating || null,
    LifecycleStatus: raw?.LifecycleStatus || raw?.lifecycleStatus || 'DRAFT',
    PublishStatus: raw?.PublishStatus || raw?.publishStatus || null,
    Policy: normalizePolicy(raw?.Policy || raw?.policy),
    StartedAt: raw?.StartedAt || raw?.startedAt || null,
    EndedAt: raw?.EndedAt || raw?.endedAt || null,
    RecordingKey: recordingKey,
    RecordingStatus: raw?.RecordingStatus || raw?.recordingStatus || null,
    RecordingDurationSeconds:
      typeof raw?.RecordingDurationSeconds === 'number'
        ? raw.RecordingDurationSeconds
        : typeof raw?.recordingDurationSeconds === 'number'
        ? raw.recordingDurationSeconds
        : undefined,
    RecordingPublishedVideoID: raw?.RecordingPublishedVideoID || raw?.recordingPublishedVideoID || null,
    createdAt: raw?.createdAt || null,
    updatedAt: raw?.updatedAt || null,
    recordingUrl: recordingKey
      ? recordingKey.startsWith('http')
        ? recordingKey
        : `${LIVE_CDN_BASE_URL}/${recordingKey}`
      : null,
    Stats: raw?.Stats || raw?.stats || null,
    host: raw?.host ? normalizeHost(raw.host) : null,
    NotificationStats: raw?.NotificationStats || raw?.notificationStats || null,
  };
};

const normalizeConfig = (raw: any): LiveAccessConfig => {
  return {
    mode: raw?.mode === 'ALLOWLIST' ? 'ALLOWLIST' : 'GLOBAL',
    allowlist: normalizeStringArray(raw?.allowlist),
    updatedAt: raw?.updatedAt,
    updatedBy: raw?.updatedBy,
  };
};

const normalizeApplication = (raw: any): LiveAccessApplication => {
  return {
    UserID: raw?.UserID || '',
    UserName: raw?.UserName || raw?.userName || null,
    Status: raw?.Status || 'PENDING',
    AppliedAt: raw?.AppliedAt || raw?.appliedAt || raw?.createdAt,
    ReviewedAt: raw?.ReviewedAt || raw?.reviewedAt || raw?.updatedAt || null,
    ReviewedBy: raw?.ReviewedBy || raw?.reviewedBy || null,
    Note: raw?.Note || raw?.note || null,
    ReapplyCount: typeof raw?.ReapplyCount === 'number' ? raw.ReapplyCount : undefined,
    AppealMessage: raw?.AppealMessage || null,
    AppealSubmittedAt: raw?.AppealSubmittedAt || null,
    Appeals: Array.isArray(raw?.Appeals)
      ? raw.Appeals.map((a: any) => ({
          message: a?.message || '',
          screenshotUrls: Array.isArray(a?.screenshotUrls) ? a.screenshotUrls : [],
          submittedAt: a?.submittedAt || '',
        }))
      : undefined,
    SuspendedAt: raw?.SuspendedAt || null,
    SuspendedBy: raw?.SuspendedBy || null,
    BlockedAt: raw?.BlockedAt || null,
    BlockedBy: raw?.BlockedBy || null,
  };
};

const normalizePolicyViolation = (raw: any): LiveAccessPolicyViolation => {
  return {
    ViolationID: raw?.ViolationID || '',
    Code: raw?.Code || '',
    Reason: raw?.Reason || '',
    Source: raw?.Source || '',
    ModeratorUserID: raw?.ModeratorUserID || null,
    VideoID: raw?.VideoID || null,
    CreatedAt: raw?.CreatedAt || null,
    ExpiresAt: raw?.ExpiresAt || null,
  };
};

const normalizeEligibilityCondition = (raw: any): LiveEligibilityCondition => {
  return {
    id: raw?.id || '',
    label: raw?.label || '',
    description: raw?.description,
    met: Boolean(raw?.met),
    statusLabel: raw?.statusLabel,
    current: typeof raw?.current === 'number' ? raw.current : undefined,
    required: typeof raw?.required === 'number' ? raw.required : undefined,
    applicationStatus: raw?.applicationStatus,
  };
};

const normalizeEligibility = (raw: any): LiveEligibilityResult => {
  return {
    canGoLive: Boolean(raw?.canGoLive),
    mode: raw?.mode === 'ALLOWLIST' ? 'ALLOWLIST' : 'GLOBAL',
    progress: {
      current: Number(raw?.progress?.current ?? 0),
      total: Number(raw?.progress?.total ?? 0),
      label: raw?.progress?.label || '',
    },
    nextAction: raw?.nextAction || '',
    application: {
      status: raw?.application?.status || 'NOT_APPLIED',
      canSubmit: Boolean(raw?.application?.canSubmit),
      isPending: Boolean(raw?.application?.isPending),
      isApproved: Boolean(raw?.application?.isApproved),
      ctaLabel: raw?.application?.ctaLabel || '',
      rejectionNote: raw?.application?.rejectionNote ?? null,
    },
    requirements: Array.isArray(raw?.requirements) ? raw.requirements.map(normalizeEligibilityCondition) : [],
    conditions: Array.isArray(raw?.conditions) ? raw.conditions.map(normalizeEligibilityCondition) : [],
  };
};

const normalizeReviewDetails = (raw: any): LiveAccessApplicationReviewDetails => {
  const sub = raw?.subscription ?? null;
  return {
    application: raw?.application ? normalizeApplication(raw.application) : null,
    profile: raw?.profile
      ? {
          UserID: raw.profile.UserID || '',
          UserName: raw.profile.UserName || null,
          Name: raw.profile.Name || null,
          ProfileImageURL: raw.profile.ProfileImageURL || null,
          FollowersCount: typeof raw.profile.FollowersCount === 'number' ? raw.profile.FollowersCount : undefined,
          AccountStatus: raw.profile.AccountStatus || undefined,
          IsPlusVerified: typeof raw.profile.IsPlusVerified === 'boolean' ? raw.profile.IsPlusVerified : undefined,
          activePolicyViolationCount: Number(raw.profile.activePolicyViolationCount ?? 0),
          videosUrl: raw.profile.videosUrl ?? null,
        }
      : null,
    eligibility: normalizeEligibility(raw?.eligibility || {}),
    activePolicyViolations: Array.isArray(raw?.activePolicyViolations)
      ? raw.activePolicyViolations.map(normalizePolicyViolation)
      : [],
    subscription: sub
      ? {
          status: sub.status ?? undefined,
          periodType: sub.periodType ?? undefined,
          subscribedAt: sub.subscribedAt ?? null,
          expiresAt: sub.expiresAt ?? null,
          isTrial: Boolean(sub.isTrial),
        }
      : null,
  };
};

const normalizeReportedSessionSummary = (raw: any): LiveReportedSessionSummary => {
  return {
    sessionId: raw?.sessionId || raw?.SessionID || '',
    hostUserId: raw?.hostUserId || raw?.HostUserID || '',
    sessionTitle: raw?.sessionTitle || raw?.SessionTitle || raw?.Title || null,
    sessionLifecycleStatus: raw?.sessionLifecycleStatus || raw?.SessionLifecycleStatus || raw?.LifecycleStatus || 'DRAFT',
    coverUrl: raw?.coverUrl || raw?.CoverUrl || null,
    startedAt: raw?.startedAt || raw?.StartedAt || null,
    endedAt: raw?.endedAt || raw?.EndedAt || null,
    reportCount: Number(raw?.reportCount ?? 0),
    reporterCount: Number(raw?.reporterCount ?? 0),
    lastReportedAt: raw?.lastReportedAt || raw?.LastReportedAt || null,
    host: normalizeHost(raw?.host),
  };
};

const normalizeReportRecord = (raw: any): LiveSessionReportRecord => {
  return {
    ReportID: raw?.ReportID || raw?.reportId || raw?._id || undefined,
    SessionID: raw?.SessionID || raw?.sessionId || '',
    HostUserID: raw?.HostUserID || raw?.hostUserId || '',
    ReporterUserID: raw?.ReporterUserID || raw?.reporterUserId || null,
    ReporterUserName: raw?.ReporterUserName || raw?.reporterUserName || null,
    ReasonCode: raw?.ReasonCode || raw?.reasonCode || null,
    ReasonLabel: raw?.ReasonLabel || raw?.reasonLabel || null,
    Details: raw?.Details || raw?.details || null,
    ScreenshotUrls: normalizeStringArray(raw?.ScreenshotUrls || raw?.screenshotUrls),
    BlockHost: Boolean(raw?.BlockHost ?? raw?.blockHost),
    SessionTitle: raw?.SessionTitle || raw?.sessionTitle || null,
    SessionLifecycleStatus: raw?.SessionLifecycleStatus || raw?.sessionLifecycleStatus || null,
    createdAt: raw?.createdAt || null,
    updatedAt: raw?.updatedAt || null,
  };
};

const normalizeReportedSessionList = (
  raw: any,
  page: number,
  limit: number,
): LiveReportedSessionsResponse => {
  const sessions = Array.isArray(raw?.sessions) ? raw.sessions.map(normalizeReportedSessionSummary) : [];
  const total = Number(raw?.total ?? sessions.length) || 0;
  const currentPage = Number(raw?.page ?? page) || page;
  const currentLimit = Number(raw?.limit ?? limit) || limit;

  return {
    sessions,
    total,
    page: currentPage,
    limit: currentLimit,
    hasMore: Boolean(raw?.hasMore ?? currentPage * currentLimit < total),
  };
};

export const getLiveAccessConfig = async (): Promise<LiveAccessConfig> => {
  const raw = await request<any>('/v1/live/access/config', undefined, 'Failed to load live access config');
  return normalizeConfig(raw);
};

export const updateLiveAccessConfig = async (payload: UpdateLiveAccessConfigPayload): Promise<LiveAccessConfig> => {
  const raw = await request<any>(
    '/v1/live/access/config',
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    'Failed to update live access config',
  );

  return normalizeConfig(raw);
};

export const listLiveAccessApplications = async (
  params: LiveAccessApplicationsParams = {},
): Promise<LiveAccessApplicationsResponse> => {
  const page = params.page ?? 1;
  const limit = params.limit ?? 10;
  const query = new URLSearchParams();

  if (params.status) query.set('status', params.status);
  if (params.search?.trim()) query.set('search', params.search.trim());
  query.set('page', String(page));
  query.set('limit', String(limit));

  const raw = await request<any>(
    `/v1/live/access/applications?${query.toString()}`,
    undefined,
    'Failed to load live access applications',
  );

  const applications = Array.isArray(raw?.applications) ? raw.applications.map(normalizeApplication) : [];
  const total = Number(raw?.total ?? raw?.pagination?.total ?? applications.length) || 0;
  const currentPage = Number(raw?.page ?? raw?.pagination?.page ?? page) || page;
  const currentLimit = Number(raw?.limit ?? raw?.pagination?.limit ?? limit) || limit;
  const totalPages = Math.max(1, Math.ceil(total / Math.max(currentLimit, 1)));

  return {
    applications,
    total,
    page: currentPage,
    limit: currentLimit,
    totalPages,
  };
};

export const reviewLiveAccessApplication = async (
  payload: ReviewLiveAccessApplicationPayload,
): Promise<LiveAccessApplication> => {
  const raw = await request<any>(
    '/v1/live/access/applications',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    'Failed to update live access application',
  );

  return normalizeApplication(raw);
};

export const sendLiveAccessNotification = async (
  userId: string,
  payload: { title: string; body: string },
): Promise<void> => {
  await request<any>(
    `/v1/live/access/applications/${encodeURIComponent(userId)}/notify`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    'Failed to send notification',
  );
};

export const getLiveAccessApplicationReviewDetails = async (
  userId: string,
): Promise<LiveAccessApplicationReviewDetails> => {
  const raw = await request<any>(
    `/v1/live/access/applications/${encodeURIComponent(userId)}`,
    undefined,
    'Failed to load live access application details',
  );

  return normalizeReviewDetails(raw);
};

export const suspendLiveAccess = async (userId: string): Promise<LiveAccessApplication> => {
  const raw = await request<any>(
    `/v1/live/access/applications/${encodeURIComponent(userId)}/suspend`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' } },
    'Failed to suspend live access',
  );
  return normalizeApplication(raw);
};

export const blockLiveAccess = async (userId: string, note: string): Promise<LiveAccessApplication> => {
  const raw = await request<any>(
    '/v1/live/access/applications/block',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, note }),
    },
    'Failed to block live access',
  );
  return normalizeApplication(raw);
};

export const liftLiveBlock = async (userId: string): Promise<LiveAccessApplication> => {
  const raw = await request<any>(
    `/v1/live/access/applications/${encodeURIComponent(userId)}/lift-block`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' } },
    'Failed to lift block',
  );
  return normalizeApplication(raw);
};

export const liftLiveSuspension = async (userId: string): Promise<LiveAccessApplication> => {
  const raw = await request<any>(
    `/v1/live/access/applications/${encodeURIComponent(userId)}/lift-suspension`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' } },
    'Failed to lift suspension',
  );
  return normalizeApplication(raw);
};

export const listLiveSessions = async (
  params: LiveSessionListParams = {},
): Promise<LiveSessionListResponse> => {
  const page = params.page ?? 1;
  const limit = params.limit ?? 20;
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (params.publishStatus) query.set('publishStatus', params.publishStatus);

  const raw = await request<any>(`/v1/live/sessions?${query.toString()}`, undefined, 'Failed to load live sessions');
  const sessions = Array.isArray(raw?.sessions) ? raw.sessions.map(normalizeSession) : [];
  const total = Number(raw?.total ?? sessions.length) || 0;
  const currentPage = Number(raw?.page ?? page) || page;
  const currentLimit = Number(raw?.limit ?? limit) || limit;

  return {
    sessions,
    total,
    page: currentPage,
    limit: currentLimit,
    hasMore: Boolean(raw?.hasMore ?? currentPage * currentLimit < total),
  };
};

export const listLiveHistory = async (
  params: { page?: number; limit?: number; hostUserId?: string } = {},
): Promise<LiveSessionListResponse> => {
  const page = params.page ?? 1;
  const limit = params.limit ?? 20;
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (params.hostUserId) {
    query.set('hostUserId', params.hostUserId);
  } else {
    query.set('adminAll', 'true');
  }

  const raw = await request<any>(`/v1/live/history?${query.toString()}`, undefined, 'Failed to load live history');
  const sessions = Array.isArray(raw?.sessions) ? raw.sessions.map(normalizeSession) : [];
  const total = Number(raw?.total ?? sessions.length) || 0;
  const currentPage = Number(raw?.page ?? page) || page;
  const currentLimit = Number(raw?.limit ?? limit) || limit;

  return {
    sessions,
    total,
    page: currentPage,
    limit: currentLimit,
    hasMore: Boolean(raw?.hasMore ?? currentPage * currentLimit < total),
  };
};

export const getLiveSession = async (sessionId: string): Promise<LiveSession> => {
  const raw = await request<any>(
    `/v1/live/sessions/${encodeURIComponent(sessionId)}`,
    undefined,
    'Failed to load live session',
  );

  return normalizeSession(raw);
};

export const listReportedLiveSessions = async (
  params: LiveReportedSessionsParams = {},
): Promise<LiveReportedSessionsResponse> => {
  const page = params.page ?? 1;
  const limit = params.limit ?? 20;
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (params.hostUserId) query.set('hostUserId', params.hostUserId);
  if (params.sessionId) query.set('sessionId', params.sessionId);

  const raw = await request<any>(`/v1/live/reports?${query.toString()}`, undefined, 'Failed to load live reports');
  return normalizeReportedSessionList(raw, page, limit);
};

export const listLiveReportedSessions = listReportedLiveSessions;

export const listUserLiveSessionsWithReports = async (
  userId: string,
  params: Pick<LiveReportedSessionsParams, 'page' | 'limit'> = {},
): Promise<LiveReportedSessionsResponse> => {
  const page = params.page ?? 1;
  const limit = params.limit ?? 20;
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  const raw = await request<any>(
    `/v1/live/users/${encodeURIComponent(userId)}/sessions?${query.toString()}`,
    undefined,
    'Failed to load user livestreams',
  );

  return normalizeReportedSessionList(raw, page, limit);
};

export const listUserLiveSessions = listUserLiveSessionsWithReports;

export const getLiveSessionReports = async (sessionId: string): Promise<LiveSessionReportsResult> => {
  const raw = await request<any>(
    `/v1/live/sessions/${encodeURIComponent(sessionId)}/reports`,
    undefined,
    'Failed to load live session reports',
  );

  return {
    session: raw?.session ? normalizeSession(raw.session) : null,
    host: normalizeHost(raw?.host),
    reportCount: Number(raw?.reportCount ?? 0),
    reporterCount: Number(raw?.reporterCount ?? 0),
    lastReportedAt: raw?.lastReportedAt || null,
    reports: Array.isArray(raw?.reports) ? raw.reports.map(normalizeReportRecord) : [],
  };
};

export const getTopReporters = async (hostUserId: string, limit = 3): Promise<TopReporter[]> => {
  const query = new URLSearchParams({ hostUserId, limit: String(limit) });
  const raw = await request<any>(`/v1/live/reports/top-reporters?${query.toString()}`, undefined, 'Failed to load top reporters');
  const reporters: TopReporter[] = Array.isArray(raw?.reporters)
    ? raw.reporters.map((r: any) => ({
        reporterUserId: String(r?.reporterUserId ?? ''),
        reporterUserName: r?.reporterUserName ? String(r.reporterUserName) : null,
        reportCount: Number(r?.reportCount ?? 0),
      }))
    : [];
  return reporters;
};

// ── Session Analytics ─────────────────────────────────────────────────────────

export interface LiveSessionNotificationStats {
  sent: boolean;
  sentAt?: string | null;
  eligibleFollowers?: number;
  totalFollowers?: number;
  devicesReached?: number;
  androidSent?: number;
  iosSent?: number;
  totalSent?: number;
  failed?: number;
  skipped?: boolean;
  skippedReason?: string | null;
}

export interface LiveSessionPollOption {
  text: string;
  votes: number;
}

export interface LiveSessionPoll {
  pollId: string;
  question: string;
  status: string;
  durationSeconds?: number | null;
  startedAt?: string | null;
  closedAt?: string | null;
  options: LiveSessionPollOption[];
}

export interface LiveSessionAnalyticsReport {
  reportId?: string;
  reporterUserId?: string | null;
  reporterUserName?: string | null;
  reasonCode?: string | null;
  reasonLabel?: string | null;
  details?: string | null;
  createdAt?: string | null;
}

export interface LiveSessionAnalyticsUserRef {
  userId: string;
  username?: string | null;
  name?: string | null;
  profileImage?: string | null;
  totalSent?: number;
  totalLikes?: number;
}

export interface LiveSessionAnalytics {
  sessionId: string;
  host: LiveHostSummary | null;
  session: {
    title?: string | null;
    category?: string | null;
    lifecycleStatus?: string;
    publishStatus?: string | null;
    startedAt?: string | null;
    endedAt?: string | null;
    durationSeconds?: number | null;
    hadPause: boolean;
    recordingStatus?: string | null;
    recordingKey?: string | null;
    recordingUrl?: string | null;
    recordingDurationSeconds?: number | null;
    policy?: LiveSessionPolicy | null;
    contentRating?: string | null;
    coverUrl?: string | null;
  };
  engagement: {
    totalUniqueViewers: number;
    totalLikes: number;
    topSenders: LiveSessionAnalyticsUserRef[];
    topLikers: LiveSessionAnalyticsUserRef[];
  };
  notifications: LiveSessionNotificationStats | null;
  polls: {
    total: number;
    items: LiveSessionPoll[];
  };
  moderation: {
    reportCount: number;
    reports: LiveSessionAnalyticsReport[];
  };
}

const normalizeAnalytics = (raw: any): LiveSessionAnalytics => {
  const normalizeUserRef = (u: any): LiveSessionAnalyticsUserRef => ({
    userId: u?.userId || u?.UserID || '',
    username: u?.username || u?.UserName || null,
    name: u?.name || u?.Name || null,
    profileImage: u?.profileImage || u?.ProfileImageURL || null,
    totalSent: typeof u?.totalSent === 'number' ? u.totalSent : undefined,
    totalLikes: typeof u?.totalLikes === 'number' ? u.totalLikes : undefined,
  });

  const normalizePoll = (p: any): LiveSessionPoll => ({
    pollId: p?.pollId || '',
    question: p?.question || '',
    status: p?.status || 'CLOSED',
    durationSeconds: typeof p?.durationSeconds === 'number' ? p.durationSeconds : null,
    startedAt: p?.startedAt || null,
    closedAt: p?.closedAt || null,
    options: Array.isArray(p?.options)
      ? p.options.map((o: any) => ({ text: o?.text || '', votes: Number(o?.votes ?? 0) }))
      : [],
  });

  const normalizeReport = (r: any): LiveSessionAnalyticsReport => ({
    reportId: r?.reportId || r?.ReportID || undefined,
    reporterUserId: r?.reporterUserId || r?.ReporterUserID || null,
    reporterUserName: r?.reporterUserName || r?.ReporterUserName || null,
    reasonCode: r?.reasonCode || r?.ReasonCode || null,
    reasonLabel: r?.reasonLabel || r?.ReasonLabel || null,
    details: r?.details || r?.Details || null,
    createdAt: r?.createdAt || null,
  });

  const s = raw?.session || {};
  const e = raw?.engagement || {};
  const n = raw?.notifications;
  const p = raw?.polls || {};
  const m = raw?.moderation || {};

  return {
    sessionId: raw?.sessionId || '',
    host: normalizeHost(raw?.host),
    session: {
      title: s?.title || null,
      category: s?.category || null,
      lifecycleStatus: s?.lifecycleStatus || 'DRAFT',
      publishStatus: s?.publishStatus || null,
      startedAt: s?.startedAt || null,
      endedAt: s?.endedAt || null,
      durationSeconds: typeof s?.durationSeconds === 'number' ? s.durationSeconds : null,
      hadPause: Boolean(s?.hadPause),
      recordingStatus: s?.recordingStatus || null,
      recordingKey: s?.recordingKey || null,
      recordingUrl: s?.recordingKey
        ? s.recordingKey.startsWith('http')
          ? s.recordingKey
          : `${LIVE_CDN_BASE_URL}/${s.recordingKey}`
        : null,
      recordingDurationSeconds: typeof s?.recordingDurationSeconds === 'number' ? s.recordingDurationSeconds : null,
      policy: normalizePolicy(s?.policy),
      contentRating: s?.contentRating || null,
      coverUrl: s?.coverUrl || null,
    },
    engagement: {
      totalUniqueViewers: Number(e?.totalUniqueViewers ?? 0),
      totalLikes: Number(e?.totalLikes ?? 0),
      topSenders: Array.isArray(e?.topSenders) ? e.topSenders.map(normalizeUserRef) : [],
      topLikers: Array.isArray(e?.topLikers) ? e.topLikers.map(normalizeUserRef) : [],
    },
    notifications: n
      ? {
          sent: Boolean(n?.sent),
          sentAt: n?.sentAt || null,
          eligibleFollowers: typeof n?.eligibleFollowers === 'number' ? n.eligibleFollowers : undefined,
          totalFollowers: typeof n?.totalFollowers === 'number' ? n.totalFollowers : undefined,
          devicesReached: typeof n?.devicesReached === 'number' ? n.devicesReached : undefined,
          androidSent: typeof n?.androidSent === 'number' ? n.androidSent : undefined,
          iosSent: typeof n?.iosSent === 'number' ? n.iosSent : undefined,
          totalSent: typeof n?.totalSent === 'number' ? n.totalSent : undefined,
          failed: typeof n?.failed === 'number' ? n.failed : undefined,
          skipped: typeof n?.skipped === 'boolean' ? n.skipped : undefined,
          skippedReason: n?.skippedReason || null,
        }
      : null,
    polls: {
      total: Number(p?.total ?? 0),
      items: Array.isArray(p?.items) ? p.items.map(normalizePoll) : [],
    },
    moderation: {
      reportCount: Number(m?.reportCount ?? 0),
      reports: Array.isArray(m?.reports) ? m.reports.map(normalizeReport) : [],
    },
  };
};

export const getLiveSessionAnalytics = async (sessionId: string): Promise<LiveSessionAnalytics> => {
  const raw = await request<any>(
    `/v1/live/sessions/${encodeURIComponent(sessionId)}/analytics`,
    undefined,
    'Failed to load livestream analytics',
  );
  return normalizeAnalytics(raw);
};

export const getLiveHealthOverview = async (): Promise<LiveHealthOverview> => {
  return reportRequest<LiveHealthOverview>(
    '/live-health/overview',
    undefined,
    'Failed to load live health overview',
  );
};

export const getLiveHealthSession = async (sessionId: string): Promise<LiveHealthSessionDetail> => {
  return reportRequest<LiveHealthSessionDetail>(
    `/live-health/sessions/${encodeURIComponent(sessionId)}`,
    undefined,
    'Failed to load live session health',
  );
};

// ── Seed Eligibility ──────────────────────────────────────────────────────────

export interface SeedEligibilityResult {
  userId: string;
  before: { canGoLive: boolean; conditions: { id: string; label: string; met: boolean }[] };
  after: { canGoLive: boolean; conditions: { id: string; label: string; met: boolean }[] };
  seeded: {
    plusVerified: boolean;
    activeSubscription: boolean;
    referralsCreated: number;
    referralsTotal: number;
  };
}

export const seedLiveEligibility = async (
  params: { userId?: string; userName?: string },
): Promise<SeedEligibilityResult> => {
  const raw = await request<any>(
    '/v1/live/access/seed-eligibility',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    },
    'Failed to seed live eligibility',
  );

  const normalizeConditions = (list: any[]) =>
    Array.isArray(list)
      ? list.map((c: any) => ({ id: c?.id || '', label: c?.label || '', met: Boolean(c?.met) }))
      : [];

  return {
    userId: raw?.userId || '',
    before: {
      canGoLive: Boolean(raw?.before?.canGoLive),
      conditions: normalizeConditions(raw?.before?.conditions ?? raw?.before?.requirements),
    },
    after: {
      canGoLive: Boolean(raw?.after?.canGoLive),
      conditions: normalizeConditions(raw?.after?.conditions ?? raw?.after?.requirements),
    },
    seeded: {
      plusVerified: Boolean(raw?.seeded?.plusVerified),
      activeSubscription: Boolean(raw?.seeded?.activeSubscription),
      referralsCreated: Number(raw?.seeded?.referralsCreated ?? 0),
      referralsTotal: Number(raw?.seeded?.referralsTotal ?? 0),
    },
  };
};
